import React, { useState, useEffect, useMemo } from "react";
import Modal from "../../components/common/modal/Modal";
import ButtonComponent from "../../components/common/button/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Dropdown from '../../components/common/Dropdown';
import { SingleCheckboxDropdown } from "../../components/common/SingleCheckboxDropdown";

const AddRestrictRuleModal = ({
    open,
    onClose,
    onSubmit,
    plantOptions,
    allData,
    editData,
    parentVendorOptions,
    childVendorOptions,
    materialOptions,
    scodeOptions,
    categoryOptions,
}) => {
    const initialRow = {
        rowKey: Date.now(),
        plantCode: null,
        parentVendorId: null,
        childVendorId: null,
        tccMaterialNumber: null,
        scode: null,
        businessCategory: null,
    };

    const [rows, setRows] = useState([initialRow]);
    const [submitLoading, setSubmitLoading] = useState(false);

    const resetForm = () => {
        setRows([
            {
                rowKey: Date.now(),
                plantCode: null,
                parentVendorId: null,
                childVendorId: null,
                tccMaterialNumber: null,
                scode: null,
                businessCategory: null,
            },
        ]);
    };

    const handleAddRow = () => {
        setRows((prev) => [
            ...prev,
            {
                rowKey: Date.now(),
                plantCode: null,
                parentVendorId: null,
                childVendorId: null,
                tccMaterialNumber: null,
                scode: null,
                businessCategory: null
            },
        ]);
    };

    const handleDelete = (index) => {
        setRows((prev) =>
            prev.filter((_, i) => i !== index)
        );
    };

    const updateField = (index, field, value) => {
        const updated = [...rows];

        updated[index][field] = value;

        switch (field) {
            case "plantCode":
                updated[index].parentVendorId = null;
                updated[index].childVendorId = null;
                updated[index].tccMaterialNumber = null;
                updated[index].scode = null;
                updated[index].businessCategory = null;
                break;

            case "parentVendorId":
                updated[index].childVendorId = null;
                updated[index].tccMaterialNumber = null;
                updated[index].scode = null;
                updated[index].businessCategory = null;
                break;

            case "childVendorId":
                updated[index].tccMaterialNumber = null;
                updated[index].scode = null;
                updated[index].businessCategory = null;
                break;

            case "tccMaterialNumber":
                updated[index].scode = null;
                updated[index].businessCategory = null;
                break;

            case "scode":
                updated[index].businessCategory = null;
                break;

            default:
                break;
        }

        setRows(updated);
    };

    const getSelectedPlantValues = (row) =>
        row.plantCode?.value ? [row.plantCode.value] : [];

    const getFilteredOptions = (row, currentField, optionList, valueKey) => {
        const filteredRows = allData.filter(r =>
            (currentField === "plantCode" ||
                !row.plantCode?.value ||
                r.plant === row.plantCode.value) &&

            (currentField === "parentVendorId" ||
                !row.parentVendorId?.value ||
                r.parentVendor === row.parentVendorId.value) &&

            (currentField === "childVendorId" ||
                !row.childVendorId?.value ||
                r.vendorCode === row.childVendorId.value) &&

            (currentField === "tccMaterialNumber" ||
                !row.tccMaterialNumber?.value ||
                r.tccMaterialNumber === row.tccMaterialNumber.value) &&

            (currentField === "scode" ||
                !row.scode?.value ||
                r.scode === row.scode.value) &&

            (currentField === "businessCategory" ||
                !row.businessCategory?.value ||
                r.businessCategory === row.businessCategory.value)
        );

        const allowedValues = new Set(
            filteredRows
                .map(r => r[valueKey])
                .filter(Boolean)
        );

        return optionList.filter(opt =>
            allowedValues.has(opt.value)
        );
    };

    const allRowsValid = rows.every(
        row =>
            row.plantCode ||
            row.parentVendorId ||
            row.childVendorId ||
            row.tccMaterialNumber ||
            row.scode
    );

    useEffect(() => {
        if (!editData) return;
        setRows([
            {
                rowKey: Date.now(),
                plantCode: editData.plantCode?.[0] || null,
                parentVendorId: editData.parentVendorId?.[0] || null,
                childVendorId: editData.childVendorId?.[0] || null,
                tccMaterialNumber: editData.tccMaterialNumber?.[0] || null,
                scode: editData.scode?.[0] || null,
                businessCategory: editData.businessCategory?.[0] || null,
            },
        ]);
    }, [editData]);

    const handleSubmit = async () => {
        try {
            setSubmitLoading(true);
            const payload = rows.map((r) => ({
                plantCode: r.plantCode?.value || "",
                plantName: r.plantCode?.label?.split(" | ")[1] || "",
                parentVendorId: r.parentVendorId?.value || "",
                parentVendorName: r.parentVendorId?.label?.split(" | ")[1] || "",
                childVendorCode: r.childVendorId?.value || "",
                childVendorName: r.childVendorId?.label?.split(" | ")[1] || "",
                materialCode: r.tccMaterialNumber?.value || "",
                scode: r.scode?.value || "",
                businessCategory: r.businessCategory?.value || "",
            }));
            const result = await onSubmit(payload);

            if (result !== false) {
                resetForm();
                onClose();
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const isRowValid = (row) => {
        const hasEntity =
            row.plantCode ||
            row.parentVendorId ||
            row.childVendorId
        return hasEntity
    };

    const isAllRowsValid = rows.every(isRowValid);

    const handleUpdateSubmit = async () => {
        try {
            setSubmitLoading(true);
            const r = rows[0];
            const payload = {
                id: editData?.id,

                plantCode: r.plantCode?.value || "",
                plantName: r.plantCode?.label?.split(" | ")[1] || "",

                parentVendorId: r.parentVendorId?.value || "",
                parentVendorName: r.parentVendorId?.label?.split(" | ")[1] || "",

                childVendorCode: r.childVendorId?.value || "",
                childVendorName: r.childVendorId?.label?.split(" | ")[1] || "",

                materialCode: r.tccMaterialNumber?.value || "",
                materialName: r.tccMaterialNumber?.label?.split(" | ")[1] || "",

                scode: r.scode?.value || "",
                businessCategory: r.businessCategory?.value || "",
            };
            const result = await onSubmit(payload);

            if (result !== false) {
                resetForm();
                onClose();
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            handleClose={() => { onClose(); resetForm() }}
            className="AddPlantModal_cls"
            title="Add Restriction"
            fullWidth={true}
            maxWidth={"lg"}
            action={
                <div style={{ display: "flex", gap: 10 }}>
                    <ButtonComponent
                        label="Back"
                        type="primary"
                        onClick={() => {
                            resetForm();
                            onClose();
                        }}
                    />

                    <ButtonComponent
                        label="Clear All"
                        type="primary"
                        onClick={resetForm}
                    />

                    <ButtonComponent
                        label={editData ? "Update" : "Submit"}
                        type="secondary"
                        disabled={!allRowsValid || submitLoading}
                        onClick={editData ? handleUpdateSubmit : handleSubmit}
                    />
                </div>
            }
            content={
                <>
                    {/* Header Row */}
                    <div
                        style={{
                            display: "flex",
                            gap: 12,
                            marginBottom: 10,
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: "14px",
                        }}
                    >
                        <div style={{ width: 50 }}>S.No</div>

                        <div style={{ flex: 1 }}>
                            Plant Code
                        </div>

                        <div style={{ flex: 1 }}>
                            Parent Vendor
                        </div>

                        <div style={{ flex: 1 }}>
                            Child Vendor
                        </div>

                        <div style={{ flex: 1 }}>
                            Material
                        </div>

                        <div style={{ flex: 1 }}>
                            External Material Group
                        </div>

                        <div style={{ flex: 1 }}>
                            Business Category
                        </div>


                        <div style={{ width: 50 }}></div>
                    </div>

                    {rows.map((row, index) => (
                        <div
                            key={row.rowKey}
                            style={{
                                display: "flex",
                                gap: 12,
                                marginBottom: 15,
                                alignItems: "center",
                            }}
                        >
                            <div style={{ width: 50 }}>
                                {index + 1}
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    height="40px"
                                    id={`plant-${index}`}
                                    name="plant"
                                    options={getFilteredOptions(
                                        row,
                                        "plantCode",
                                        plantOptions,
                                        "plant"
                                    )}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    selectedItemsData={row.plantCode}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "plantCode", items)
                                    }
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`parent-${index}`}
                                    name="parent"
                                    height={40}
                                    options={getFilteredOptions(
                                        row,
                                        "parentVendorId",
                                        parentVendorOptions,
                                        "parentVendor"
                                    )}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    selectedItemsData={row.parentVendorId}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "parentVendorId", items)
                                    }
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`child-${index}`}
                                    name="child"
                                    height={40}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    options={getFilteredOptions(
                                        row,
                                        "childVendorId",
                                        childVendorOptions,
                                        "vendorCode"
                                    )}
                                    selectedItemsData={row.childVendorId}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "childVendorId", items)
                                    }
                                    copyPasteDisabled={false}
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`material-${index}`}
                                    name="material"
                                    height={40}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    options={getFilteredOptions(
                                        row,
                                        "tccMaterialNumber",
                                        materialOptions,
                                        "tccMaterialNumber"
                                    )}
                                    selectedItemsData={row.tccMaterialNumber}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "tccMaterialNumber", items)
                                    }
                                    copyPasteDisabled={false}
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`socode-${index}`}
                                    name="scode"
                                    height={40}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    options={getFilteredOptions(
                                        row,
                                        "scode",
                                        scodeOptions,
                                        "scode"
                                    )}
                                    selectedItemsData={row.scode}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "scode", items)
                                    }
                                    copyPasteDisabled={false}
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`cat-${index}`}
                                    name="category"
                                    options={getFilteredOptions(
                                        row,
                                        "businessCategory",
                                        categoryOptions,
                                        "businessCategory"
                                    )}
                                    height={40}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    selectedItemsData={row.businessCategory}
                                    copyPasteDisabled={false}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "businessCategory", items)
                                    }
                                />
                            </div>
                            <div style={{ width: 50 }}>
                                {/* <IconButton
                                    onClick={() => handleDelete(index)}
                                    disabled={rows.length === 1}
                                >
                                    <DeleteIcon />
                                </IconButton>*/}
                            </div>
                        </div>
                    ))}

                    {/*!editData && <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 15,
                        }}
                    >
                        <ButtonComponent
                            type="primary"
                            label="+ Add Configuration"
                            onClick={handleAddRow}
                            disabled={!isAllRowsValid}
                        />
                    </div>*/}
                </>
            }
        />
    );
};

export default AddRestrictRuleModal;